/**
 * Shared entity emission helpers: layer/color resolution and the R13+
 * entity prologue (handle → owner → AcDbEntity subclass → layer → color)
 * every serializer starts with.
 */

import type { Entity } from '../../../../state/store';
import { hexToAci, hexToInt } from '../colors';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';

/** Resolve the DXF layer NAME an entity draws on. */
export function layerNameOf(ctx: DxfWriteContext, e: Entity): string {
    const entry = ctx.layerEntries.get(e.layer);
    return entry?.layer.name ?? e.layer ?? '0';
}

/** Entity-level linetype override (BYLAYER/BYBLOCK fall back to the layer). */
export function linetypeNameOf(ctx: DxfWriteContext, e: Entity): string | null {
    const raw = String(e.data.linetype ?? '').toUpperCase();
    if (!raw || raw === 'BYLAYER' || raw === 'BYBLOCK') return null;
    return ctx.ltypeEntries.has(raw) ? raw : null;
}

/**
 * Write the common entity prefix:
 * 0 TYPE / 5 handle / 330 owner / 100 AcDbEntity / 8 layer / 6 linetype /
 * 62 ACI / 420 true color. Returns the allocated handle.
 */
export function entityPrologue(
    w: DxfStreamWriter,
    ctx: DxfWriteContext,
    e: Entity,
    dxfType: string,
    owner: string,
    layerName = layerNameOf(ctx, e)
): string {
    const handle = ctx.handles.entityHandle(e.id);
    w.pair(0, dxfType).pair(5, handle).pair(330, owner);
    w.pair(100, 'AcDbEntity');
    w.pair(8, layerName);
    const ltype = linetypeNameOf(ctx, e);
    if (ltype) w.pair(6, ltype);
    const color = (e.data as Record<string, unknown>).color;
    if (typeof color === 'string' && color.startsWith('#')) {
        const aci = hexToAci(color);
        if (aci !== 256) w.pair(62, aci);
        const rgb = hexToInt(color);
        if (rgb !== null && ctx.version.trueColor) w.pair(420, rgb);
    }
    return handle;
}

/** Write only the color groups (62 when not ByLayer, 420 only AC1018+). */
export function emitColorGroups(w: DxfStreamWriter, ctx: DxfWriteContext, color: unknown): void {
    if (typeof color !== 'string' || !color.startsWith('#')) return;
    const aci = hexToAci(color);
    if (aci !== 256) w.pair(62, aci);
    const rgb = hexToInt(color);
    if (rgb !== null && ctx.version.trueColor) w.pair(420, rgb);
}
